import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { warehousesService, Warehouse } from '../services/warehousesService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import {
  Plus,
  Edit,
  Search,
  Warehouse as WarehouseIcon,
  Trash2,
  AlertTriangle,
  MapPin,
  Phone,
} from 'lucide-react';

interface WarehouseListProps {
  refreshTrigger?: number;
  onCreateClick: () => void;
  onEditClick: (warehouse: Warehouse) => void;
}

export const ModernWarehouseList: React.FC<WarehouseListProps> = ({
  refreshTrigger,
  onCreateClick,
  onEditClick,
}) => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();

  // Permission checks
  const canCreate = hasPermission('warehouses', 'create');
  const canEdit = hasPermission('warehouses', 'edit');
  const canDelete = hasPermission('warehouses', 'delete');

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<Warehouse | null>(null);

  const loadWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await warehousesService.getWarehouses();
      setWarehouses(data);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load warehouses'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses, refreshTrigger]);

  const handleDelete = async () => {
    if (!warehouseToDelete) return;

    try {
      await warehousesService.deleteWarehouse(warehouseToDelete.id);
      setWarehouses((prev) =>
        prev.filter((w) => w.id !== warehouseToDelete.id)
      );
      setDeleteDialogOpen(false);
      setWarehouseToDelete(null);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Delete warehouse error:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
        ? String(err.message)
        : 'Failed to delete warehouse';
      setError(errorMessage);
      // Keep dialog open so user can see the error
    }
  };

  const filteredWarehouses = warehouses.filter((warehouse) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      warehouse.name.toLowerCase().includes(searchLower) ||
      warehouse.address.toLowerCase().includes(searchLower) ||
      warehouse.contact.toLowerCase().includes(searchLower) ||
      warehouse.description.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <WarehouseIcon className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.warehouses.name', 'Warehouses')}
          </h1>
        </div>
        {canCreate && (
          <Button onClick={onCreateClick} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>{translate('pos.warehouses.add', 'Add Warehouse')}</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>{translate('common.search', 'Search')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translate(
                'pos.warehouses.search',
                'Search warehouses...'
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Warehouse Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.warehouses.list', 'Warehouse List')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredWarehouses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? translate('pos.warehouses.empty', 'No warehouses found')
                : translate('pos.warehouses.empty', 'No warehouses yet')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{translate('pos.warehouses.fields.name', 'Name')}</TableHead>
                  <TableHead>{translate('pos.warehouses.fields.address', 'Address')}</TableHead>
                  <TableHead>{translate('pos.warehouses.fields.contact', 'Contact')}</TableHead>
                  <TableHead>{translate('pos.warehouses.fields.location', 'Location')}</TableHead>
                  <TableHead className="text-right">
                    {translate('common.actions', 'Actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWarehouses.map((warehouse) => (
                  <TableRow key={warehouse.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{warehouse.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {warehouse.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span data-sensitive-field="address">
                          {warehouse.address}
                        </span>
                      </div>
                      {warehouse.additionalAddressInfo && (
                        <div className="text-sm text-muted-foreground mt-1">
                          <span data-sensitive-field="address">
                          {warehouse.additionalAddressInfo}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{warehouse.contact}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {warehouse.location.latitude && warehouse.location.longitude ? (
                        <span className="text-sm">
                          {warehouse.location.latitude.toFixed(4)}, {warehouse.location.longitude.toFixed(4)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditClick(warehouse)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setWarehouseToDelete(warehouse);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.warehouses.delete.title', 'Delete Warehouse')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.warehouses.delete.description',
                'Are you sure you want to delete this warehouse? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          {warehouseToDelete && (
            <div className="py-4">
              <p className="font-medium">{warehouseToDelete.name}</p>
              <p className="text-sm text-muted-foreground">
                {warehouseToDelete.address}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setWarehouseToDelete(null);
              }}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

